// Upgrade rules and costs (§31 P3, design "How to upgrade a piece").
//
// A piece takes a `PIECE_VARIANTS` row on an **upgrade site** (S5's
// `upgradeSiteError`) and the bill is paid by **one source**:
//
// - **A forge-capable piece of yours touching it** (itself not counted), out
//   of its own banks — input, then output, cheapest unit first.
// - **An Armory of yours within `POWER.armoryReach`**, which is sealed and
//   holds nothing — it pays from your stores nearest it (S3's order).
//
// Plus `RULES.upgradeFuel` points from the same source: its own pool
// (`IndustryState.fuel`, a refiner's), then the power network reaching it
// (`drawNetworkFuel` — generators, then batteries), then coal it holds, one
// point each — a refiner's exchange. Sources are tried forgers first, then
// Armories, each by square; the first that covers bill *and* fuel pays.
//
// Shared by `upgrade` and the Signet Crown (§31 C5), so the card's Matriarch
// is priced and sited by the same rule. Deterministic: every sweep sorted.

import { type Color, fileOf, pieceColor, pieceType, rankOf } from "../board";
import { type Bill, type BillRow, COAL, FUEL, variantBill } from "../materials";
import { pieceAt, type Position, variantAt } from "../position";
import {
  ARTISAN,
  type Grade,
  POWER,
  pieceVariant,
  RULES,
  VARIANT_PLAIN,
  variantsFor
} from "../rules";
import { isCoal, isFoundryAt } from "./machines";
import { drawNetworkFuel, networkFuel } from "./power";
import {
  type Bank,
  bufferAtLeast,
  bufferTotal,
  fuelAt,
  type IndustryState,
  setFuel,
  structureAt,
  structureSquares,
  takeUnitOf,
  unitAt,
  unitMeets,
  ARTISAN_PILLAR,
  makeUnit,
  unitValue
} from "./state";
import { poweredCount } from "./gear";
import { actsAsArmory, neighbours, storesNearest, takeStockOf, upgradeSiteError } from "./structures";

/** Why this piece may not take a variant row, or null. */
export type UpgradeError =
  | "not-your-piece"
  | "cannot-climb"
  | "not-that-rank"
  | "too-poor"
  // A variant is bought once. See `canTakeVariant`.
  | "already-a-variant"
  // Not on an upgrade site (§31 S5): no Armory stands, or none in reach.
  | "no-armory"
  | "out-of-armory-reach"
  // A source could pay the bill, but none has the fuel points too (§31 P3).
  | "no-fuel"
  // A row nothing pays for — the Matriarch's, which the Signet Crown grants.
  | "not-for-sale";

/**
 * What row `id` costs `owner` (§31 P3, A6): `variantBill`, less
 * `ARTISAN.upgradeDiscount` off the **dearest** row (by `unitValue`, the first
 * on a tie) while the side's Artisan Pillar is on — the design's "one less of
 * their highest resource". A row brought to nought is dropped.
 */
export function upgradeBill(industry: IndustryState, owner: Color, id: number): Bill {
  const bill = variantBill(id);
  if (bill.length === 0 || poweredCount(industry, owner, ARTISAN_PILLAR) === 0) return bill;
  const price = (row: BillRow): number => unitValue(makeUnit(row.grade, 0, row.line as 0));
  let dearest = 0;
  for (let i = 1; i < bill.length; i++) if (price(bill[i]) > price(bill[dearest])) dearest = i;
  return bill
    .map((row, i) => (i === dearest ? { ...row, count: row.count - ARTISAN.upgradeDiscount } : row))
    .filter((row) => row.count > 0);
}

/** The banks a forger pays an upgrade out of — an oven's goods land in `out`. */
const FORGER_BANKS: readonly Bank[] = ["in", "out"];

/** Is a forge-capable piece of `owner`'s touching `at` — itself not counted (§31 S5)? */
export function forgerBeside(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color
): boolean {
  return forgersBeside(position, industry, at, owner).length > 0;
}

/** `owner`'s forge-capable pieces touching `at`, by square. */
function forgersBeside(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color
): number[] {
  return neighbours(at)
    .filter((sq) => {
      const piece = pieceAt(position, sq);
      return piece !== 0 && pieceColor(piece) === owner && isFoundryAt(position, industry, sq);
    })
    .sort((a, b) => a - b);
}

const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * Who may pay an upgrade at `at`, in the order asked: forgers touching it,
 * then Armories within `POWER.armoryReach`, each by square.
 */
export function upgradeSources(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color
): number[] {
  const armories = structureSquares(industry).filter((sq) => {
    const one = structureAt(industry, sq)!;
    return actsAsArmory(one.kind) && one.owner === owner && chebyshev(sq, at) <= POWER.armoryReach;
  });
  return [...forgersBeside(position, industry, at, owner), ...armories];
}

const isArmory = (industry: IndustryState, sq: number): boolean => {
  const kind = structureAt(industry, sq)?.kind;
  return kind !== undefined && actsAsArmory(kind);
};

/** How much of one bill row `source` can pay. */
export function sourceFunds(
  industry: IndustryState,
  source: number,
  owner: Color,
  row: BillRow
): number {
  if (isArmory(industry, source)) {
    let total = 0;
    for (const store of storesNearest(industry, owner, source)) {
      for (const unit of structureAt(industry, store)!.stock) {
        if (unitMeets(unit, row.line, row.grade)) total++;
      }
    }
    return total;
  }
  let total = 0;
  for (const bank of FORGER_BANKS) {
    total += bufferAtLeast(industry, source, row.grade as Grade, bank, row.line);
  }
  return total;
}

/** Coal in a forger's banks — what it may burn for points. */
function coalAt(industry: IndustryState, sq: number): number {
  let held = 0;
  for (const bank of FORGER_BANKS) {
    for (let i = 0; i < bufferTotal(industry, sq, bank); i++) {
      if (isCoal(unitAt(industry, sq, i, bank))) held++;
    }
  }
  return held;
}

/**
 * Fuel points `source` could give an upgrade now: its pool, the network
 * reaching it, coal it holds. An Armory has no pool and holds no coal.
 *
 * **A coal unit is counted once.** A forger whose bill row could be paid in
 * coal does not exist (no bill names the fuel line), so the two never share.
 */
export function sourceFuel(industry: IndustryState, source: number, owner: Color): number {
  const network = networkFuel(industry, owner, source);
  if (isArmory(industry, source)) return network;
  return fuelAt(industry, source) + network + coalAt(industry, source);
}

/** The first row `source` is short of, with what it has, or null. */
export function shortRow(
  industry: IndustryState,
  source: number,
  owner: Color,
  bill: Bill
): { row: BillRow; have: number } | null {
  for (const row of bill) {
    const have = sourceFunds(industry, source, owner, row);
    if (have < row.count) return { row, have };
  }
  return null;
}

/**
 * The source that would pay `bill` plus fuel at `at`, or why none can:
 * `too-poor` when no source covers the bill, `no-fuel` when one does but
 * none has the points too. The site is the caller's question.
 */
export function pickUpgradeSource(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  bill: Bill
): { source: number } | { why: "too-poor" | "no-fuel" } {
  let rich = false;
  for (const source of upgradeSources(position, industry, at, owner)) {
    if (shortRow(industry, source, owner, bill)) continue;
    rich = true;
    if (sourceFuel(industry, source, owner) >= RULES.upgradeFuel) return { source };
  }
  return { why: rich ? "no-fuel" : "too-poor" };
}

/**
 * Pay `bill` and the fuel out of `source`. The caller has checked
 * `pickUpgradeSource`; the bill is spent row by row, cheapest unit first.
 */
export function payUpgrade(
  industry: IndustryState,
  source: number,
  owner: Color,
  bill: Bill
): void {
  const armory = isArmory(industry, source);
  for (const row of bill) {
    for (let i = 0; i < row.count; i++) {
      if (armory) {
        for (const store of storesNearest(industry, owner, source)) {
          if (takeStockOf(structureAt(industry, store)!, row.line, row.grade)) break;
        }
        continue;
      }
      // Input first, then output: what it was handed before what it made.
      for (const bank of FORGER_BANKS) {
        if (takeUnitOf(industry, source, row.line, row.grade, bank) >= 0) break;
      }
    }
  }

  let owed = RULES.upgradeFuel;
  if (!armory) {
    const pool = Math.min(owed, fuelAt(industry, source));
    setFuel(industry, source, fuelAt(industry, source) - pool);
    owed -= pool;
  }
  owed -= drawNetworkFuel(industry, owner, source, owed);
  for (const bank of FORGER_BANKS) {
    while (owed > 0 && !armory && takeUnitOf(industry, source, FUEL, COAL, bank) >= 0) owed--;
  }
}

/**
 * The upgrade site, the bill and the fuel for row `id` on `at` — the half
 * `upgrade` and the Signet Crown share. Null when it can be paid.
 */
export function upgradeCostError(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  id: number
): "no-armory" | "out-of-armory-reach" | "too-poor" | "no-fuel" | null {
  const site = upgradeSiteError(industry, owner, at, forgerBeside(position, industry, at, owner));
  if (site) return site;
  const pick = pickUpgradeSource(position, industry, at, owner, upgradeBill(industry, owner, id));
  return "why" in pick ? pick.why : null;
}

/**
 * May this piece take this variant row?
 *
 * **The material half only**: whether the *army* may is a fact about its
 * rules and lives in the reducer — the planner asks this about candidates and
 * must not be handed a deck to do it.
 *
 * **And the piece must be plain** — `variantsFor` answers by rank and a Marshal
 * is still a `PAWN`, so without it the rows are a re-spec you pay to swap
 * between forever.
 */
export function canTakeVariant(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  id: number
): UpgradeError | null {
  const piece = pieceAt(position, at);
  if (!piece || pieceColor(piece) !== owner) return "not-your-piece";
  if (variantAt(position, at) !== VARIANT_PLAIN) return "already-a-variant";
  const options = variantsFor(pieceType(piece));
  if (options.length === 0) return "cannot-climb";
  if (!options.includes(id)) return "not-that-rank";
  // **A row something else grants is not for sale at any price** — the
  // Matriarch, whose card pays her bill through `payVariant` itself.
  if (pieceVariant(id).grantedBy) return "not-for-sale";
  return upgradeCostError(position, industry, at, owner, id);
}

/** Pay row `id`'s bill and fuel for the piece on `at`. The caller has checked. */
export function payVariant(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  id: number
): void {
  const bill = upgradeBill(industry, owner, id);
  const pick = pickUpgradeSource(position, industry, at, owner, bill);
  if ("why" in pick) throw new Error(`cannot pay for row ${id}: ${pick.why}`);
  payUpgrade(industry, pick.source, owner, bill);
}
